import React, { useState, useCallback, useEffect } from "react";
import {
  FilePlus2,
  LoaderCircle,
  CircleCheckBig,
  FileWarning,
  RefreshCcw,
  AlertTriangle,
  Clock,
  Zap,
} from "lucide-react";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import KnowledgeBaseSelector from "./KnowledgeBaseSelector";
import {
  generateKnowledgeBasePresignedUploadUrl,
  getFile,
  getKnowledgeBase,
} from "../../../../service/apiService";
import { useUser } from "../../../../context/UserContext";
import { FileStatus } from "../../../../types";
import { addFileToKnowledgeBase } from "../../../../helpers/knowledgeBaseLocalStorage";
import { isPDFScanned } from "../../../../helpers/pdfValidation";
import { toast } from "sonner";

interface KnowledgeBase {
  id: string;
  name: string;
  index_name: string;
}

interface KnowledgeBaseDetails {
  id: string;
  knowledgebase_name: string;
  index_name: string;
  files: Array<{
    id: string;
    file_name: string;
    status: string;
    created_by: string;
    created_at: string;
  }>;
}

interface FileUploadStatus {
  file: File;
  progress: number;
  status: "uploading" | "uploaded" | "not_processed" | "processing" | "processed" | "failed" | "duplicate";
  errorMessage?: string;
  fileId?: string;
  uploadStarted?: boolean;
  useAdvancedProcessing?: boolean;
}

// Processing options
type ProcessingMode = "standard" | "advanced";

interface ProcessingOption {
  id: ProcessingMode;
  name: string;
  description: string;
  icon: React.ComponentType<any>;
  processingTime: string;
  recommended?: boolean;
}

const processingOptions: ProcessingOption[] = [
  {
    id: "standard",
    name: "Standard Processing",
    description: "Quick text extraction without AI enhancement.",
    icon: Clock,
    processingTime: "Quick processing"
  },
  {
    id: "advanced",
    name: "Advanced Processing (AI Enhanced)",
    description: "AI-powered extraction with table analysis, image recognition, and deep content understanding.",
    icon: Zap,
    processingTime: "Queued processing (takes longer)",
    recommended: true
  },
];

const statusLabels = {
  uploading: "Uploading...",
  uploaded: "Uploaded",
  not_processed: "In Queue",
  processing: "Processing",
  processed: "Processed Successfully",
  failed: "Failed",
  duplicate: "Duplicate File",
};

interface UploadFilesProps {
  refresh: number;
  preSelectedKnowledgeBase?: {
    id: string;
    name: string;
    index_name: string;
  };
}

const UploadFiles: React.FC<UploadFilesProps> = ({ 
  refresh: externalRefresh, 
  preSelectedKnowledgeBase 
}) => {  const [selectedKnowledgeBase, setSelectedKnowledgeBase] = useState<KnowledgeBase | null>(null);
  const [knowledgeBaseDetails, setKnowledgeBaseDetails] = useState<KnowledgeBaseDetails | null>(null);
  const [fileUploadStatuses, setFileUploadStatuses] = useState<FileUploadStatus[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoadingKnowledgeBase, setIsLoadingKnowledgeBase] = useState(false);
  const [textContent, setTextContent] = useState('');
  const [duplicateFileNames, setDuplicateFileNames] = useState<Set<string>>(new Set());
  const [defaultProcessingMode, setDefaultProcessingMode] = useState<ProcessingMode>("standard");
  const { userId } = useUser();

  // Auto-select knowledge base if preSelected
useEffect(() => {
  if (preSelectedKnowledgeBase && !selectedKnowledgeBase) {
    setSelectedKnowledgeBase(preSelectedKnowledgeBase);
  }
}, [preSelectedKnowledgeBase]);

  // Fetch knowledge base details when a knowledge base is selected
  useEffect(() => {
    const fetchKnowledgeBaseDetails = async () => {
      if (!selectedKnowledgeBase) {
        setKnowledgeBaseDetails(null);
        return;
      }
      
      setIsLoadingKnowledgeBase(true);
      try {
        const response = await getKnowledgeBase(selectedKnowledgeBase.id);
        setKnowledgeBaseDetails(response);
        
        // Create a set of existing file names for quick duplicate checking.
        // New-scheme rows keep the user's name in original_file_name (file_name
        // is the minted storage name); older rows only have file_name.
        if (response.files && Array.isArray(response.files)) {
          const existingFileNames = new Set<string>(
            response.files.map(
              (file: { file_name: string; original_file_name?: string }) =>
                (file.original_file_name || file.file_name).toLowerCase()
            )
          );
          setDuplicateFileNames(existingFileNames);
        }
      } catch (error) {
        console.error("Error fetching knowledge base details:", error);
        toast.error("Failed to load BrainBank details");
      } finally {
        setIsLoadingKnowledgeBase(false);
      }
    };

    fetchKnowledgeBaseDetails();
  }, [selectedKnowledgeBase, refreshKey, externalRefresh]);

  // Check if file already exists in the knowledge base
  const checkIfFileExists = (fileName: string): boolean => {
    if (!duplicateFileNames.size) return false;
    return duplicateFileNames.has(fileName.toLowerCase());
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const processedFiles: FileUploadStatus[] = [];
    
    for (const file of acceptedFiles) {
      const isDuplicate = checkIfFileExists(file.name);
      
      // Check if PDF is scanned (only for non-duplicate PDFs)
      if (!isDuplicate && file.type === "application/pdf") {
        const isScanned = await isPDFScanned(file);
        
        if (isScanned && defaultProcessingMode === "standard") {
          toast.error(
            `"${file.name}" appears to be a scanned PDF. Please use Advanced Processing mode for better results.`
          );
          // Skip adding this file to the upload queue
          continue;
        }
      }
      
      // Add file to queue (whether duplicate or valid for upload)
      processedFiles.push({
        file,
        progress: 0,
        status: isDuplicate ? "duplicate" : "uploading",
        uploadStarted: false,
        useAdvancedProcessing: defaultProcessingMode === "advanced",
        errorMessage: isDuplicate ? "This file already exists in the BrainBank. Please delete the existing file before uploading again." : undefined
      });
    }
    
    setFileUploadStatuses((prev) => [...prev, ...processedFiles]);
  }, [duplicateFileNames, defaultProcessingMode]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "application/pdf": [".pdf"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
      "text/plain": [".txt"]
    },
    maxFiles: 20,
    disabled: !selectedKnowledgeBase || fileUploadStatuses.length >= 20 || isLoadingKnowledgeBase,
    onDrop,
  });

  useEffect(() => {
    const filesToUpload = fileUploadStatuses.filter(
      (status) => status.status === "uploading" && !status.uploadStarted
    );

    if (filesToUpload.length > 0) {
      setFileUploadStatuses((prevStatuses) =>
        prevStatuses.map((status) =>
          filesToUpload.some((fileToUpload) => fileToUpload.file === status.file)
            ? { ...status, uploadStarted: true }
            : status
        )
      );

      filesToUpload.forEach((fileStatus) => {
        handleFileUpload(fileStatus.file);
      });
    }
  }, [fileUploadStatuses]);

  useEffect(() => {
    if (refreshKey > 0) {
      refreshFileStatuses();
    }
  }, [refreshKey, externalRefresh]);

  const handleTextUpload = async () => {
    if (!selectedKnowledgeBase || !textContent.trim()) return;

    try {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const fileName = `text_document_${timestamp}.txt`;
      
      // Check if a text file with this name already exists
      const isDuplicate = checkIfFileExists(fileName);
      if (isDuplicate) {
        toast.error("A file with this name already exists in the BrainBank. Please try again with a different timestamp.");
        return;
      }
      
      const textBlob = new Blob([textContent], { type: 'text/plain' });
      const textFile = new File([textBlob], fileName, { type: 'text/plain' });

      const newFileStatus: FileUploadStatus = {
        file: textFile,
        progress: 0,
        status: 'uploading',
        uploadStarted: true,
        useAdvancedProcessing: defaultProcessingMode === "advanced"
      };

      setFileUploadStatuses(prev => [...prev, newFileStatus]);
      
      await handleFileUpload(textFile);
      
      setTextContent('');

    } catch (error) {
      console.error('Error uploading text:', error);
    }
  };

  async function handleFileUpload(file: File) {
    if (!selectedKnowledgeBase) return;

    // One more check before upload in case the state was updated
    const isDuplicate = checkIfFileExists(file.name);
    if (isDuplicate) {
      setFileUploadStatuses((prevStatuses) =>
        prevStatuses.map((status) =>
          status.file === file
            ? {
                ...status,
                status: "duplicate",
                errorMessage: "This file already exists in the BrainBank. Please delete the existing file before uploading again."
              }
            : status
        )
      );
      return;
    }

    // Get the processing mode for this specific file
    const fileStatus = fileUploadStatuses.find(status => status.file === file);
    const useAdvancedProcessing = fileStatus?.useAdvancedProcessing ?? false;

    try {
      const presignedUrlResponse = await generateKnowledgeBasePresignedUploadUrl(
        file.name,
        selectedKnowledgeBase.index_name,
        selectedKnowledgeBase.id,
        defaultProcessingMode
      );

      const { presigned_url, file_id } = presignedUrlResponse;

      await axios.put(presigned_url, file, {
        headers: {
          "Content-Type": file.type || "application/octet-stream",
          "x-amz-meta-created_by": userId,
          "x-amz-meta-updated_by": userId,
          "x-amz-meta-index_name": selectedKnowledgeBase.index_name,
          "x-amz-meta-file_id": file_id,
          "x-amz-meta-processing_mode": defaultProcessingMode
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setFileUploadStatuses((prevStatuses) =>
            prevStatuses.map((status) =>
              status.file === file
                ? { ...status, progress: percentCompleted }
                : status
            )
          );
        },
      });

      setFileUploadStatuses((prevStatuses) =>
        prevStatuses.map((status) =>
          status.file === file
            ? { ...status, status: "uploaded", progress: 100, fileId: file_id }
            : status
        )
      );

      // Add the file name to the duplicates set to prevent immediate re-uploads
      setDuplicateFileNames(prev => {
        const newSet = new Set(prev);
        newSet.add(file.name.toLowerCase());
        return newSet;
      });

      fetchFileStatus(file_id, file);

    } catch (error) {
      console.error("Error uploading file:", error);
      setFileUploadStatuses((prevStatuses) =>
        prevStatuses.map((status) =>
          status.file === file
            ? {
                ...status,
                status: "failed",
                errorMessage: "Failed to upload file. Please try again.",
              }
            : status
        )
      );
    }
  }

  const fetchFileStatus = async (fileId: string, file: File) => {
    try {
      const response = await getFile(fileId);
      addFileToKnowledgeBase(response);
      const fileStatus = response.status ? response.status.toString() as FileUploadStatus['status'] : "failed";

      setFileUploadStatuses((prevStatuses) =>
        prevStatuses.map((status) =>
          status.file === file
            ? {
                ...status,
                status: fileStatus,
                errorMessage: fileStatus === "failed" ? "Processing failed" : undefined,
              }
            : status
        )
      );
    } catch (error) {
      console.error("Error fetching file status:", error);
      setFileUploadStatuses((prevStatuses) =>
        prevStatuses.map((status) =>
          status.file === file
            ? {
                ...status,
                status: "failed",
                errorMessage: "Failed to fetch file status.",
              }
            : status
        )
      );
    }
  };

  const refreshFileStatuses = async () => {
    setIsRefreshing(true);
    try {
      const promises = fileUploadStatuses
        .filter(status => status.fileId && status.status !== "uploading" && status.status !== "duplicate")
        .map(status => fetchFileStatus(status.fileId!, status.file));
      
      await Promise.all(promises);
    } catch (error) {
      console.error("Error refreshing file statuses:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleRetry = (file: File) => {
    // If it was a duplicate, check if it's still a duplicate
    const isDuplicate = checkIfFileExists(file.name);
    
    setFileUploadStatuses((prevStatuses) =>
      prevStatuses.map((status) =>
        status.file === file
          ? {
              ...status,
              status: isDuplicate ? "duplicate" : "uploading",
              progress: 0,
              errorMessage: isDuplicate 
                ? "This file already exists in the BrainBank. Please delete the existing file before uploading again."
                : undefined,
              uploadStarted: !isDuplicate ? false : undefined
            }
          : status
      )
    );
  };

  const handleRefresh = () => {
    setRefreshKey(prevKey => prevKey + 1);
  };

  const handleKnowledgeBaseSelect = (knowledgeBase: KnowledgeBase | null) => {
    setSelectedKnowledgeBase(knowledgeBase);
    // Reset file statuses when changing knowledge base
    setFileUploadStatuses([]);
  };
  
  const handleClearAll = () => {
    setFileUploadStatuses([]);
  };

  const uploadedFiles = fileUploadStatuses.filter(
    status => status.status !== "uploading"
  );

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "uploading":
        return <LoaderCircle className="h-4 w-4 text-blue-500 animate-spin" />;
      case "uploaded":
        return <CircleCheckBig className="h-4 w-4 text-blue-600" />;
      case "processing":
        return <LoaderCircle className="h-4 w-4 text-yellow-500 animate-spin" />;
      case "processed":
        return <CircleCheckBig className="h-4 w-4 text-green-600" />;
      case "not_processed":
        return <LoaderCircle className="h-4 w-4 text-gray-400" />;
      case "failed":
        return <FileWarning className="h-4 w-4 text-red-500" />;
      case "duplicate":
        return <AlertTriangle className="h-4 w-4 text-amber-500" />;
      default:
        return null;
    }
  };

  // Calculate the count of duplicate files in current selection
  const duplicateCount = fileUploadStatuses.filter(status => status.status === "duplicate").length;

  return (
    <div className="py-2">
      <div className="mt-2 text-sm mx-2 p-4 rounded-lg bg-white">
        {!preSelectedKnowledgeBase && (
          <KnowledgeBaseSelector
            userId={userId}
            refresh={externalRefresh}
            onSelect={handleKnowledgeBaseSelect}
          />
        )}

        {preSelectedKnowledgeBase && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Selected BrainBank
            </label>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <span className="text-sm font-medium text-gray-900">
                {preSelectedKnowledgeBase.name}
              </span>
            </div>
          </div>
        )}

        {/* Processing Mode Selection */}
<div className="mt-6">
  <label className="block text-sm font-medium text-gray-700 mb-3">
    Default Processing Mode for Files
  </label>
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    {processingOptions.map((option) => {
      const IconComponent = option.icon;
      const isSelected = defaultProcessingMode === option.id;
      
      return (
        <div
          key={option.id}
          className={`relative cursor-pointer rounded-lg border p-4 transition-all ${
            isSelected
              ? "border-violet-500 bg-violet-50 ring-0 ring-violet-500"
              : "border-gray-200 bg-white hover:border-gray-300"
          }`}
          onClick={() => setDefaultProcessingMode(option.id)}
        >
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <IconComponent
                className={`h-6 w-6 mt-1 ${
                  isSelected ? "text-gray-600" : "text-gray-400"
                }`}
              />
            </div>
            <div className="ml-3 flex-1 overflow-hidden">
              <div className="flex items-center flex-wrap gap-1">
                <h3
                  className={`text-sm font-medium ${
                    isSelected ? "text-gray-900" : "text-gray-900"
                  }`}
                >
                  {option.name}
                </h3>
                {option.recommended && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Recommended
                  </span>
                )}
              </div>
              <p
                className={`mt-1 text-xs break-words ${
                  isSelected ? "text-gray-700" : "text-gray-500"
                }`}
              >
                {option.description}
              </p>
              <p
                className={`mt-2 text-xs font-medium ${
                  isSelected ? "text-gray-600" : "text-gray-400"
                }`}
              >
                Processing time: {option.processingTime}
              </p>
            </div>
            <input
              type="radio"
              name="processing-mode-upload"
              value={option.id}
              checked={isSelected}
              onChange={() => setDefaultProcessingMode(option.id)}
              className="h-4 w-4 text-violet-600 border-gray-300 focus:ring-violet-500"
            />
          </div>
        </div>
      );
    })}
  </div>
</div>
        {isLoadingKnowledgeBase && (
          <div className="mt-4 text-center text-gray-500">
            <LoaderCircle className="h-5 w-5 animate-spin mx-auto mb-2" />
            <p>Loading BrainBank details...</p>
          </div>
        )}

        {/* File Upload Area */}
        <div
          {...getRootProps({
            className: `dropzone mt-4 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md ${
              isDragActive
                ? "border-violet-500"
                : selectedKnowledgeBase && !isLoadingKnowledgeBase
                ? "border-gray-300 cursor-pointer"
                : "border-gray-200 bg-gray-100 cursor-not-allowed"
            }`,
          })}
          style={{
            pointerEvents: selectedKnowledgeBase && !isLoadingKnowledgeBase ? "auto" : "none",
          }}
        >
          <input
            {...getInputProps()}
            disabled={!selectedKnowledgeBase || isLoadingKnowledgeBase}
            accept=".pdf,.xlsx,.docx,.txt"
          />
          <div className="space-y-1 text-center">
            <FilePlus2 className="mx-auto h-12 w-12 text-gray-400" />
            <div className="flex text-sm text-gray-600">
              <label
                htmlFor="file-upload"
                className={`cursor-pointer ${
                  selectedKnowledgeBase && !isLoadingKnowledgeBase
                    ? "text-violet-600 hover:text-violet-500"
                    : "text-gray-400"
                }`}
              >
                <span>Upload files</span>
              </label>
              <p className="pl-1">or drag and drop</p>
            </div>
            <ul className="list-disc list-inside text-xs text-gray-500 text-left">
              <li>Maximum 20 files at a time</li>
              <li>Accepted files: PDF, DOCX, XLSX and TXT</li>
              <li>Duplicate files will be detected and flagged</li>
              <li>Scanned PDFs require Advanced Processing mode</li>
            </ul>
          </div>
        </div>

        {/* Text Input Area */}
        <div className={`
          mt-4 border-2 border-dashed rounded-md p-4 
          ${!selectedKnowledgeBase || isLoadingKnowledgeBase ? 'bg-gray-100' : ''}
        `}>
          <div className="space-y-1 text-center mb-3">
            <div className="flex text-sm text-gray-600 justify-center">
              <p>Insert Text</p>
            </div>
          </div>
          <textarea
            value={textContent}
            onChange={(e) => setTextContent(e.target.value)}
            placeholder="Type or paste your text here..."
            className={`
              w-full h-32 p-3 border rounded-md resize-none
              ${!selectedKnowledgeBase || isLoadingKnowledgeBase ? 'bg-gray-100 cursor-not-allowed' : ''}
              focus:outline-none focus:ring-2 focus:ring-violet-500
            `}
            disabled={!selectedKnowledgeBase || isLoadingKnowledgeBase}
          />
          <div className="flex justify-end mt-3">
            <button
              onClick={handleTextUpload}
              disabled={!selectedKnowledgeBase || !textContent.trim() || isLoadingKnowledgeBase}
              className={`
                px-4 py-2 rounded-md text-sm font-medium
                ${!selectedKnowledgeBase || !textContent.trim() || isLoadingKnowledgeBase
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-violet-600 text-white hover:bg-violet-700'
                }
              `}
            >
              Upload
            </button>
          </div>
        </div>

        {/* Duplicate files warning */}
        {duplicateCount > 0 && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md flex items-start">
            <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-amber-800">Duplicate Files Detected</h3>
              <p className="mt-1 text-xs text-amber-700">
                {duplicateCount === 1 
                  ? "1 file you're trying to upload already exists in this BrainBank." 
                  : `${duplicateCount} files you're trying to upload already exist in this BrainBank.`
                } Please delete existing files before uploading again.
              </p>
            </div>
          </div>
        )}

        {/* Files currently uploading */}
        <div className="mt-4">
          {fileUploadStatuses
            .filter(status => status.status === "uploading")
            .map((fileStatus, index) => (
              <div key={`uploading-${index}`} className="mt-2 text-xs text-gray-500">
                {fileStatus.file.name} - Uploading... {fileStatus.progress}%
                <div className="w-full bg-gray-200 rounded-full h-2.5 mt-1">
                  <div
                    className="bg-violet-600 h-2.5 rounded-full"
                    style={{ width: `${fileStatus.progress}%` }}
                  ></div>
                </div>
              </div>
            ))}
        </div>

        {/* Files Status Table */}
        {uploadedFiles.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-700">File Processing Status</h3>
              <div className="flex space-x-2">
                <button
                  onClick={handleClearAll}
                  className="pr-5 inline-flex items-center text-xs text-red-600 hover:text-red-500"
                >
                  Clear All
                </button>
                <button
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className="inline-flex items-center text-xs text-violet-600 hover:text-violet-500 disabled:text-gray-400"
                >
                  <RefreshCcw className={`h-4 w-4 mr-1 ${isRefreshing ? 'animate-spin' : ''}`} />
                  Refresh Status
                </button>
              </div>
            </div>
            <div className="bg-blue-50 p-3 rounded-md mb-3 text-xs text-blue-800 flex items-start">
              <div className="flex-shrink-0 mt-0.5">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-2">
                <p>File synchronization with BrainBank happens in the background and will continue even if you close this panel. Please check back later to see the final processing status.</p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      File Name
                    </th>
                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {uploadedFiles.map((fileStatus, index) => (
                    <tr key={`status-${index}`}>
                      <td className="px-4 py-2 whitespace-nowrap text-xs text-gray-700 truncate max-w-xs">
                        {fileStatus.file.name}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-xs">
                        <div className="flex items-center">
                          {getStatusIcon(fileStatus.status)}
                          <span className={`ml-2 ${fileStatus.status === "duplicate" ? "text-amber-700" : ""}`}>
                            {statusLabels[fileStatus.status]}
                          </span>
                        </div>
                        {fileStatus.errorMessage && (
                          <p className={`text-xs mt-1 ${fileStatus.status === "duplicate" ? "text-amber-600" : "text-red-500"}`}>
                            {fileStatus.errorMessage}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-xs">
                        {fileStatus.status === "failed" && (
                          <button
                            onClick={() => handleRetry(fileStatus.file)}
                            className="text-violet-600 hover:text-violet-800"
                          >
                            Retry
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadFiles;